import { FC } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../../../../common/utils";
import { CarbonSnapResultsResponse } from "../../../../../../../datasources/carbonsnap/io/response";
import { TruckIcon, TrainIcon, ShipIcon, PlaneIcon } from "../../../../../../../assets/icons";

interface Props {
	vehicleDetails: CarbonSnapResultsResponse["vehicleDetails"];
}

const VehicleTab: FC<Props> = ({ vehicleDetails }) => {
	const { t } = useTranslation("whatIf.carbonsnap");

	const vehicleTypes = [
		{ key: "road" as const, label: t("results/details/vehicle/road"), Icon: TruckIcon },
		{ key: "train" as const, label: t("results/details/vehicle/train"), Icon: TrainIcon },
		{ key: "sea" as const, label: t("results/details/vehicle/sea"), Icon: ShipIcon },
		{ key: "air" as const, label: t("results/details/vehicle/air"), Icon: PlaneIcon },
	];

	const formatValue = (value: number, measure: string) => {
		return `${formatNumber(value, "decimal", 2, 2)} ${measure}`;
	};

	return (
		<Stack direction="column" spacing={2}>
			{vehicleTypes.map(({ key, label, Icon }) => {
				const details = vehicleDetails[key];
				if (!details) return null;

				const metrics = [
					{
						label: t("results/details/vehicle/distance"),
						value: details.distance.value,
						measure: details.distance.measure,
					},
					{
						label: t("results/details/vehicle/transport emissions"),
						value: details.transportEmissions.value,
						measure: details.transportEmissions.measure,
					},
					{
						label: t("results/details/vehicle/hub emissions"),
						value: details.hubEmissions.value,
						measure: details.hubEmissions.measure,
					},
					{
						label: t("results/details/vehicle/emission intensity"),
						value: details.emissionIntensity.value,
						measure: details.emissionIntensity.measure,
					},
					{
						label: t("results/details/vehicle/biogenic emissions"),
						value: details.biogenicEmissions.value,
						measure: details.biogenicEmissions.measure,
					},
				];

				return (
					<Paper sx={{ p: 2 }} key={key}>
						<Stack direction="row" spacing={2} alignItems="flex-start">
							<Box>
								<Icon fontSize="large" color="primary" />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="h6" sx={{ mb: 2 }}>
									{label}
								</Typography>
								<Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
									{metrics.map((metric) => (
										<Box key={metric.label}>
											<Typography variant="caption" color="text.secondary">
												{metric.label}
											</Typography>
											<Typography variant="body1" fontWeight="medium">
												{formatValue(metric.value, metric.measure)}
											</Typography>
										</Box>
									))}
								</Stack>
							</Box>
						</Stack>
					</Paper>
				);
			})}
		</Stack>
	);
};

export default VehicleTab;
